import { Link, LoaderFunctionArgs, useLoaderData, useRevalidator } from 'react-router-dom'
import { useCurrentHealthCenter, useIsAccountClosed } from '../../components/AppShell'
import { ButtonLink, Panel, PanelBody, PanelHeading, useConfirm } from '../../components/common'
import { useToast } from '../../components/common'
import { TreatmentTemplate, deleteTreatmentTemplate, fetchTreatmentTemplates } from '../../api/treatments'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.body !== null && typeof error.body === 'object') {
    const messages = Object.entries(error.body as Record<string, string[]>).map(
      ([field, fieldMessages]) => `${field} ${fieldMessages.join(', ')}`,
    )
    if (messages.length > 0) {
      return messages.join(' · ')
    }
  }
  return 'No pudimos completar la operación. Intenta nuevamente.'
}

export function loader({ params }: LoaderFunctionArgs): Promise<readonly TreatmentTemplate[]> {
  return fetchTreatmentTemplates(Number(params.healthCenterId))
}

export default function TreatmentTemplatesIndexPage() {
  const healthCenter = useCurrentHealthCenter()
  const closed = useIsAccountClosed()
  const confirm = useConfirm()
  const showToast = useToast()
  const templates = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const { revalidate } = useRevalidator()

  if (!healthCenter) {
    return null
  }

  const remove = async (template: TreatmentTemplate) => {
    if (await confirm({ title: 'Eliminar', message: 'Estas seguro?' })) {
      try {
        await deleteTreatmentTemplate(healthCenter.id, template.id)
      } catch (error) {
        showToast('error', getErrorMessage(error))
        return
      }
      trackEvent('Treatment Template Deleted')
      void revalidate()
    }
  }

  return (
    <Panel>
      <PanelHeading className="text-center">
        <h1>Lista de Tratamientos del Centro</h1>
      </PanelHeading>
      <PanelBody className="container">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Codigo</th>
              <th>Precio</th>
              <th>Nota</th>
              <th colSpan={closed ? 1 : 3} />
            </tr>
          </thead>
          <tbody>
            {templates.map((template) => (
              <tr key={template.id} data-testid={`row-${template.id}`}>
                <td>{template.name}</td>
                <td>{template.code}</td>
                <td>${template.price}</td>
                <td>{template.note}</td>
                <td>
                  <Link className="card-link" to={`/health_centers/${healthCenter.id}/treatment_templates/${template.id}`}>
                    Detalle
                  </Link>
                </td>
                {!closed && (
                  <>
                    <td>
                      <Link className="card-link" to={`/health_centers/${healthCenter.id}/treatment_templates/${template.id}/edit`}>
                        Modificar
                      </Link>
                    </td>
                    <td>
                      <a className="card-link" onClick={() => void remove(template)}>
                        Eliminar
                      </a>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        <br />

        {!closed && <ButtonLink to={`/health_centers/${healthCenter.id}/treatment_templates/new`}>Nuevo Tratamiento</ButtonLink>}
      </PanelBody>
    </Panel>
  )
}
